'use client'

import { useMemo } from 'react'
import Link from 'next/link'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'

import { Card } from '../../interfaces/season.interface'

interface CardsRenderProps {
  readonly cards: Card[]
  readonly selectedCollection?: string
  readonly selectedTheme?: string
}

function CardsRender({ cards, selectedCollection, selectedTheme }: CardsRenderProps): React.ReactNode {
  const isCardSelected = useMemo(() => {
    return (card: Card): boolean => {
      const urlParts = card.url.split('?')
      if (urlParts.length < 2) return false

      const searchParams = new URLSearchParams(urlParts[1])
      const cardCollection = searchParams.get('tempCategory')
      const cardTheme = searchParams.get('theme')

      if (selectedCollection && cardCollection) {
        return selectedCollection === cardCollection
      }

      if (selectedTheme && cardTheme) {
        return selectedTheme === cardTheme
      }

      return false
    }
  }, [selectedCollection, selectedTheme])

  return (
    <>
      <div className='w-full pl-3 md:-pr-4 mt-5 md:mt-10 md:pb-4'>
        <Carousel
          opts={{
            align: 'start',
          }}
          className='w-full md:w-auto md:flex md:justify-center'
        >
          <CarouselContent className='md:pb-2'>
            {cards.map((card, index) => {
              const isSelected = isCardSelected(card)
              const isPriority = index < 4
              const cardImage = (
                <picture
                  className={`block rounded-lg overflow-hidden${isSelected ? '' : ' bg-default-50'}`}
                >
                  <source
                    srcSet={card.image}
                    type='image/webp'
                    className='w-full h-full'
                  />
                  <img
                    src={card.image}
                    alt={card.title}
                    className='w-full h-full object-fill rounded-lg md:!w-48 md:!h-60'
                    fetchPriority={isPriority ? 'high' : 'auto'}
                    loading={isPriority ? 'eager' : 'lazy'}
                  />
                </picture>
              )

              return (
                <CarouselItem
                  key={`${card.id}-${card.title}`}
                  className='w-full h-full max-w-28 max-h-44 md:max-w-48 md:max-h-60 relative px-2 py-2 md:px-3 md:pt-3 md:pb-10'
                >
                  <div className={isSelected ? 'relative z-10' : 'relative'}>
                    <Link
                      href={card.url}
                      type='button'
                      className='block relative'
                    >
                      {isSelected ? (
                        <div className='game-card-shine is-selected flex items-center bg-gradient-to-tr from-primary-500 to-primary-700 rounded-lg p-0.5 relative overflow-hidden'>
                          <div className='bg-default-50 rounded-lg relative z-10'>
                            {cardImage}
                          </div>
                        </div>
                      ) : (
                        cardImage
                      )}
                      <p className='text-sm md:text-base font-bold text-default-900 absolute bottom-3 md:bottom-7 -ml-2 text-center w-full z-20'>
                        {card.title}
                      </p>
                    </Link>
                  </div>
                </CarouselItem>
              )
            })}
          </CarouselContent>
        </Carousel>
      </div>
    </>
  )
}

export default CardsRender
